'use client'

import { useCurrentLocationStore } from '@/entities/place'
import useSearchAddress from '@/features/seoPlaceSearch/model/useSearchAddress'
import { AddressSearchField } from '@/features/seoPlaceSearch/ui/AddressSearchField'
import { AddressSearchList } from '@/features/seoPlaceSearch/ui/AddressSearchList'
import { IconCurrentLocation } from '@/shared/assets/icons'
import { SolidButton } from '@/shared/design-system/components/Button/Solid'
import {
  LeftAlignedHeader,
  HeaderRightTextButton,
} from '@/shared/design-system/components/Header'
import { logEvent } from '@/shared/external/firebase'
import { EVENT_NAMES, LABELS } from '@/shared/lib'
import { useLockBodyScroll } from '@/shared/model'
import { useOutsideTouch } from '@/shared/model/useOutsideTouch'

const AddressSearchBottomSheet = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  useLockBodyScroll(isOpen)

  const {
    data: { addressDocuments },
    state: {
      currentValue,
      inputRef,
      hasMoreAddresses,
      hasSearchError,
      isLoadingMoreAddresses,
      isLoadingAddressList,
    },
    actions: {
      handleAddressSelection,
      handleClickCurrentLocation,
      handleChangeInput,
      handleClearInput,
      loadMoreAddresses,
      retryAddressSearch,
    },
  } = useSearchAddress()

  const { setCurrentLocationState } = useCurrentLocationStore()

  const ref = useOutsideTouch(() => {
    if (inputRef.current && document.activeElement === inputRef.current) {
      inputRef.current.blur()
    }
  })

  return (
    <div className='w-full h-full flex flex-col'>
      {/* dimmed */}
      {/* make overlay fixed and ensure it has a higher z-index than other content (e.g. swiper) */}
      <div
        className='fixed inset-0 bg-background-overlay-strong z-[10000]'
        onClick={onClose}
      />

      {/* 바텀 시트 영역 */}
      {/* make bottom sheet fixed and put it above overlay with slightly higher z-index */}
      <div className='fixed w-full h-[calc(100dvh-40px)] bg-background-surface-floating top-dimension-x10 left-0 rounded-t-dimension-x5 flex flex-col md:w-[500px] md:max-h-[780px] md:rounded-dimension-x5 md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-[10001]'>
        <LeftAlignedHeader
          title={
            <LeftAlignedHeader.Title.Text
              text='원하는 위치를 검색해주세요'
              typography='heading-3-bold'
            />
          }
          right={<HeaderRightTextButton text='닫기' onClickClose={onClose} />}
        />

        <div className='w-full px-dimension-x5 flex flex-col gap-dimension-x4 pb-dimension-x4'>
          {/* 주소 검색 필드 */}
          <AddressSearchField
            inputRef={inputRef}
            currentValue={currentValue}
            handleChangeInput={handleChangeInput}
            handleClearInput={handleClearInput}
            handleClickInput={() => {
              setCurrentLocationState({
                isAccessDenied: false,
              })

              logEvent(EVENT_NAMES.WEB_VIEW.SEO.CLICK.SEARCH_ITEM, {
                label: LABELS.WEB_VIEW.SEO.CLICK.SEARCH_ADDRESS_KEYWORD_INPUT,
              })
            }}
          />

          {/* 현재 위치로 찾기 버튼 */}
          <SolidButton
            text='현재 위치로 찾기'
            clickable={handleClickCurrentLocation}
            variant='neutral-weak'
            size='medium'
            enabled
            leadingIcon={
              <IconCurrentLocation fill='#505C66' width='18' height='18' />
            }
          />
        </div>

        {/* 검색 결과 */}
        <div ref={ref} className='overflow-y-scroll flex flex-col flex-1'>
          <AddressSearchList
            searchResults={addressDocuments}
            keyword={currentValue}
            hasMoreAddresses={hasMoreAddresses}
            hasSearchError={hasSearchError}
            loadMoreAddresses={loadMoreAddresses}
            retryAddressSearch={retryAddressSearch}
            isLoadingMoreAddresses={isLoadingMoreAddresses}
            isLoadingAddressList={isLoadingAddressList}
            onClickAddressItem={handleAddressSelection}
            onCloseBottomSheet={onClose}
          />
        </div>
      </div>
    </div>
  )
}

export default AddressSearchBottomSheet
